<template>
  <div
    class="relative w-full h-full shadow rounded-md text-mouse bg-depth-1"
    :data-testid="testId"
  >
    <div class="absolute inset-0 rounded-md overflow-hidden">
      <strapi-image
        v-if="backgroundImage?.url"
        :image="backgroundImage"
        image-class="w-full"
        alt-fallback="Order Configuration Background"
        :lazy="false"
        fluid
      />
      <div
        v-if="isDiscountedGame"
        class="absolute right-0 top-10 px-2 py-0.5 font-extrabold rounded-l-md bg-danger text-mouse"
      >
        {{ discountedPercentageText }}
      </div>
    </div>

    <div
      v-if="hasHighlightSlot"
      class="absolute w-full rounded-t bg-(image:--color-gradient-rainbow) text-onyx"
    >
      <slot name="highlight"></slot>
    </div>

    <!-- No `role="checkbox"`: the highlight is a visual affordance on a
         non-focusable container that only mouse clicks can toggle (the card
         click handler lives on the parent), and it wraps its own interactive
         "Order now" control. Announcing an inoperable checkbox to assistive
         technology is worse than announcing nothing — the per-card order
         button stays fully keyboard accessible either way. -->
    <div
      class="relative flex flex-col justify-between h-full rounded-md p-4 border-2 border-transparent transition-[border-color] duration-200"
      :class="{
        'border-brand': selected,
      }"
      :data-selected="selected"
    >
      <div class="grow">
        <slot name="default"></slot>
      </div>

      <div v-if="hasButtonSlot" class="mt-2 grid">
        <slot name="button"></slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { StrapiImage } from '~/apollo/types/types';

const props = defineProps<{
  backgroundImage: StrapiImage;
  selected?: boolean;
  isDiscountedGame?: boolean;
  discountedPercentage?: number;
  /** Optional Playwright locator hook (e.g. `landing-page-plan-card-0`). */
  testId?: string;
}>();

const slots = useSlots();

const hasButtonSlot = ref<boolean>(!!slots.button);
const hasHighlightSlot = ref<boolean>(!!slots.highlight);
const discountedPercentageText = computed<string>(
  () => `- ${Number(props.discountedPercentage ?? 0)}%`,
);
</script>
